/**
 * CP-468: control de saldo por procesador (Dashboard v1, alcance definido por
 * Pablo García el 12/10: solo control de saldos, sin tabs).
 *
 * Por procesador compara el saldo real de la cuenta en el banco contra lo que
 * les debemos a los collectors que cobran en esa cuenta ("Neto" de
 * GET /monitoreo/collectors = disponible + pendiente × (1 − charge_rate)):
 *   - Bind: cuenta por defecto (BindPSP 20-1-777983-1-5) → collectors AR#BIND
 *     SIN cuenta propia (`account` vacío). Los de cuenta propia no tienen saldo
 *     consultable y quedan afuera.
 *   - CMF: cuenta recaudadora 30054402 → collectors AR#CMFP de la cuenta
 *     compartida (CUENTA_CMF_COMPARTIDA).
 * Diferencia = saldo banco − Neto. En v1 incluye las comisiones ya cobradas
 * (v2 las va a restar), así que lo esperable es una diferencia positiva; una
 * diferencia NEGATIVA significa que en el banco hay menos de lo que se debe.
 *
 * Sin imports de runtime para que corra con `node --test`.
 */

export interface CollectorSaldoRow {
  collector:  string;
  processor:  string;
  account:    string;
  disponible: number;
  pendiente:  number;
  bruto:      number;
  neto:       number;
}

export interface ResumenCollectors {
  collectors:      number;   // collectors del universo (con o sin saldo)
  conSaldo:        number;   // con disponible o pendiente ≠ 0
  disponible:      number;
  pendiente:       number;
  bruto:           number;
  neto:            number;
  comisionPendiente: number; // bruto − neto (comisión estimada sobre lo pendiente)
}

export type Universo = (r: CollectorSaldoRow) => boolean;

export const ACQ_BIND = "AR#BIND";

/** Collectors AR#BIND que cobran en la cuenta por defecto de BindPSP. */
export const universoBind: Universo = (r) => r.processor === ACQ_BIND && !(r.account ?? "").trim();

/** Collectors AR#CMFP de la cuenta compartida (recaudadora). */
export const universoCmf = (cuentaCompartida: string, acq = "AR#CMFP"): Universo =>
  (r) => r.processor === acq && r.account === cuentaCompartida;

export function resumir(rows: CollectorSaldoRow[], universo: Universo): ResumenCollectors {
  const t: ResumenCollectors = { collectors: 0, conSaldo: 0, disponible: 0, pendiente: 0, bruto: 0, neto: 0, comisionPendiente: 0 };
  for (const r of rows) {
    if (!universo(r)) continue;
    t.collectors += 1;
    if (r.disponible !== 0 || r.pendiente !== 0) t.conSaldo += 1;
    t.disponible += r.disponible; t.pendiente += r.pendiente;
    t.bruto += r.bruto; t.neto += r.neto;
  }
  t.comisionPendiente = t.bruto - t.neto;
  return t;
}

export type NivelDiferencia = "ok" | "warn" | "bad";

export interface Diferencia {
  monto: number;          // saldo − neto
  pct:   number | null;   // monto / saldo (null si saldo = 0)
  nivel: NivelDiferencia;
}

/**
 * Umbral (en % del saldo del banco) a partir del cual la diferencia positiva se
 * marca en amarillo. Pendiente de definir por Pablo → mientras sea null solo se
 * colorea la diferencia negativa (rojo).
 */
export const UMBRAL_DIFERENCIA_PCT: number | null = null;

export function diferencia(saldo: number, neto: number, umbralPct: number | null = UMBRAL_DIFERENCIA_PCT): Diferencia {
  const monto = saldo - neto;
  const pct = saldo !== 0 ? monto / saldo : null;
  const nivel: NivelDiferencia =
    monto < 0 ? "bad"
    : umbralPct != null && pct != null && Math.abs(pct) * 100 > umbralPct ? "warn"
    : "ok";
  return { monto, pct, nivel };
}
